import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, useBlocker, useNavigate } from '@tanstack/react-router'
import {
  ChevronDownIcon,
  ExternalLinkIcon,
  EyeOffIcon,
  HistoryIcon,
  LinkIcon,
  PanelRightIcon,
  Trash2Icon,
} from 'lucide-react'

import { cn } from 'cn'
import { isPermanentCollection } from '@ems/cms/permanence'
import { Alert, AlertDescription, AlertTitle } from '@ems/ui/components/alert'
import { Button } from '@ems/ui/components/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@ems/ui/components/dropdown-menu'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@ems/ui/components/tabs'
import { ConfirmDialog } from '@ems/ui/patterns/confirm-dialog'
import { PageHeader } from '@ems/ui/patterns/page-header'
import { toast } from '@ems/ui/components/sonner'

import type { CrumbValue } from '#admin/components/crumbs.ts'
import {
  collectionAnchor,
  formFields,
  publishDocument,
  requestPreview,
  rollbackDocument,
  siteUrl,
  saveDocument,
  scheduleDocument,
  statusLine,
  unpublishDocument,
  type CmsDocument,
  type Collection,
} from '#admin/features/cms/api.ts'
import { documentMeta } from '#admin/features/cms/document-meta.ts'
import { DocumentSidebar } from '#admin/features/cms/document-sidebar.tsx'
import { ErrorSummary } from '#admin/features/cms/error-summary.tsx'
import { EmbeddedCollectionSection } from '#admin/features/cms/embedded-collection-section.tsx'
import { FieldGroups, FieldRows, titledGroupCount } from '#admin/features/cms/field-groups.tsx'
import { fieldIssuesOf, withoutField, type FieldIssues } from '#admin/features/cms/field-issues.ts'
import { pageSources } from '#admin/features/cms/page-sources.ts'
import { SECTION_INFO } from '@ems/cms/layouts'
import { pageLayoutOf } from '#admin/features/cms/page-layout.ts'
import { PageLayoutEditor } from '#admin/features/cms/page-layout-editor.tsx'
import {
  normalizeSeo,
  SEO_FIELDS,
  seoFallbacks,
  seoFieldName,
  type Seo,
  type SeoKey,
} from '#admin/features/cms/seo.ts'
import { SeoEditor } from '#admin/features/cms/seo-editor.tsx'
import { useDocumentDraft } from '#admin/features/cms/use-document-draft.ts'
import { SitePreviewLayout } from '#admin/features/cms/site-preview-layout.tsx'
import { useElementWidth } from '#admin/features/cms/use-element-width.ts'
import { useSitePreview } from '#admin/features/cms/use-site-preview.ts'
import { VersionHistoryDialog } from '#admin/features/cms/version-history-dialog.tsx'
import {
  collectionsQuery,
  documentQuery,
  documentsQuery,
  editionLocalesQuery,
  siteQuery,
  versionsQuery,
} from '#admin/features/cms/queries.ts'
import { ErrorView } from '#admin/components/error-view.tsx'
import { toastError } from '#admin/lib/toast-error.ts'
import { sessionQuery } from '#admin/features/auth/queries.ts'
import { can } from '#admin/features/auth/session.ts'
import { ReadOnlyNotice } from '#admin/components/read-only-notice.tsx'
import { DocumentEditorSkeleton } from '#admin/features/cms/content-skeletons.tsx'
import {
  DeleteDocumentDialog,
  focusCollection,
} from '#admin/features/cms/delete-document-dialog.tsx'
import { deleteOffer } from '#admin/features/cms/deletion.ts'
import { saveThenPublish } from '#admin/features/cms/save-then-publish.ts'

/**
 * The editor.
 *
 * The form is rendered from the collection descriptor the API served, so this file contains no
 * field names and no per-collection branches. What it does contain is the publishing workflow,
 * which is the part an editor actually has to reason about — and three decisions in it are worth
 * stating.
 *
 * **The draft is local until Save.** Every keystroke going to the server would mean an editor's
 * half-finished paragraph is what a colleague sees in the list, and it would make
 * `expectedVersion` meaningless.
 *
 * **`expectedRevision` travels with every save.** Two editors in one document is the ordinary
 * state of a content team the week before an event; without it the second save silently wins and
 * the first editor's paragraphs are gone with nothing to look at. `revision` moves on every save,
 * and `version` only on a publish, so `expectedVersion` alone did not catch it.
 *
 * **Publishing says what it invalidated.** The most common support question about any CMS is "I
 * published it and the site still shows the old one", and the honest answer is a list of paths.
 *
 * **The layout has two columns: the content, and facts about the document.** The form is on the
 * left, in numbered cards, with SEO on its own tab. The other collections that fill the page are
 * cards in the form too. The page settings, the status, the languages and the release time are
 * on the right. The version history is a dialog that the
 * header opens. When the site preview is open, the right column moves under the form, so the
 * screen has two parts: the editor and the preview.
 */
export const Route = createFileRoute('/_authenticated/cms/content/$id')({
  pendingComponent: DocumentEditorSkeleton,
  staticData: { crumb: useDocumentCrumb },
  loader: async ({ context, params }) => {
    await Promise.all([
      context.queryClient.ensureQueryData(collectionsQuery()),
      context.queryClient.ensureQueryData(documentQuery(params.id)),
      context.queryClient.ensureQueryData(versionsQuery(params.id)),
      context.queryClient.ensureQueryData(editionLocalesQuery()),
      context.queryClient.ensureQueryData(documentsQuery()),
    ])
  },
  component: EditorPage,
  errorComponent: ({ error, reset }) => <ErrorView error={error} reset={reset} />,
})

function documentHeading(document: CmsDocument, collection: Collection | undefined): string {
  if (document.title !== '') return document.title
  return collection === undefined ? 'Untitled' : `New ${collection.singular.toLowerCase()}`
}

function useDocumentCrumb(): CrumbValue {
  const { id } = Route.useParams()
  const { data: document } = useQuery(documentQuery(id))
  const { data: collections } = useQuery(collectionsQuery())
  const collection = collections?.items.find((c) => c.key === document?.collection)
  const label = document === undefined ? 'Document' : documentHeading(document, collection)
  if (collection === undefined) return label
  return {
    parents: [
      { label: collection.label, to: '/cms/content', hash: collectionAnchor(collection.key) },
    ],
    label,
  }
}

const NO_ERRORS: Readonly<Record<string, string>> = {}

/** The editor width (px) under which the details column goes above the form: `@4xl`, 56rem. */
const STACKED_BELOW_PX = 896

type EditorTab = 'content' | 'seo'

/** The tab that holds a field. The SEO block's fields are named `seo.title` and so on. */
function tabOf(name: string): EditorTab {
  return name.startsWith('seo.') ? 'seo' : 'content'
}

function EditorPage() {
  const { id } = Route.useParams()
  const queryClient = useQueryClient()

  const { data: collections } = useSuspenseQuery(collectionsQuery())
  const { data: document } = useSuspenseQuery(documentQuery(id))
  // Read-only for a role with `cms.view`: the document and its history, and no verb. Preview goes
  // too — issuing a preview link is a write on the server.
  const { data: session } = useSuspenseQuery(sessionQuery())
  const canManage = can(session, 'cms.manage')
  const { data: versions } = useSuspenseQuery(versionsQuery(id))

  const collection = collections.items.find((c) => c.key === document.collection)

  // `data` and `seo` are one draft, saved together; see `useDocumentDraft` for why `dirty` is
  // tracked per edit rather than derived per render.
  const {
    data: draft,
    seo: seoDraft,
    dirty,
    dirtyRef,
    onFieldChange: changeField,
    onSeoChange: changeSeo,
    markClean,
  } = useDocumentDraft(document)
  /**
   * A page's layout: its sections, and which of its fields they show (`page-layout.ts`). Null for
   * a collection with a fixed layout. Read from the draft, so a section added a moment ago shows
   * its fields before the page is saved.
   */
  const layout = useMemo(
    () => (collection === undefined ? null : pageLayoutOf(collection, draft, document.slug)),
    [collection, draft, document.slug],
  )
  /** The fields the editor shows now. A refusal is matched against these. */
  const fields = useMemo(
    () =>
      collection === undefined
        ? []
        : layout === null
          ? formFields(collection.fields)
          : layout.visible,
    [collection, layout],
  )
  const [issues, setIssues] = useState<FieldIssues | null>(null)
  const [refusals, setRefusals] = useState(0)
  const [tab, setTab] = useState<EditorTab>('content')

  // The SEO block's inputs are named `seo.title` and so on, so a refusal can land on them — but
  // only for a collection that shows them. With a layout, every field of the form: a required
  // field in a removed section still refuses a publish, and the summary must name it.
  const issueFields = useMemo(
    () => [
      ...(layout === null ? fields : layout.all),
      ...(collection?.seo === true ? SEO_FIELDS : []),
    ],
    [collection, fields, layout],
  )

  const onFieldChange = useCallback(
    (name: string, next: unknown) => {
      changeField(name, next)
      setIssues((was) => withoutField(was, name))
    },
    [changeField],
  )

  const onSeoChange = useCallback(
    <K extends SeoKey>(key: K, next: Seo[K]) => {
      changeSeo(key, next)
      setIssues((was) => withoutField(was, seoFieldName(key)))
    },
    [changeSeo],
  )

  const showRefusal = (error: unknown) => {
    const found = fieldIssuesOf(
      error,
      issueFields.map((field) => field.name),
    )
    if (found === null) {
      toastError(error)
      return
    }
    setIssues(found)
    setRefusals((count) => count + 1)
    // Open a tab that has a problem. A refusal about fields on a hidden tab looks like no refusal.
    const refused = Object.keys(found.fields).map(tabOf)
    if (refused.length > 0 && !refused.includes(tab)) {
      setTab(refused.includes('content') ? 'content' : 'seo')
    }
  }

  /**
   * Two guards, because there are two ways to leave.
   *
   * `useBlocker` catches a router navigation — clicking Content in the breadcrumb, or anything
   * in the sidebar. `beforeunload` catches closing the tab and the back button. Neither covers
   * the other, and losing an afternoon's editing to the one that was not covered is the same
   * loss either way.
   *
   * `withResolver` replaces `globalThis.confirm`. The native dialog is unthemed, unstyled and
   * blocks the main thread; this hands back `proceed` and `reset` so the same decision is made in
   * a dialog that matches the rest of the console and can be canceled with Escape.
   */
  const blocker = useBlocker({
    shouldBlockFn: () => dirtyRef.current,
    enableBeforeUnload: false,
    // Without this the hook returns nothing and can only answer synchronously — which is why the
    // original had to reach for `globalThis.confirm`.
    withResolver: true,
  })

  useEffect(() => {
    if (!dirty) return undefined

    const warn = (event: BeforeUnloadEvent) => {
      // `preventDefault` is what triggers the browser's own dialog; the text is the browser's,
      // not ours, and has been for a decade.
      event.preventDefault()
    }
    globalThis.addEventListener('beforeunload', warn)
    return () => globalThis.removeEventListener('beforeunload', warn)
  }, [dirty])

  const invalidate = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['cms', 'document', id] }),
      queryClient.invalidateQueries({ queryKey: ['cms', 'versions', id] }),
      queryClient.invalidateQueries({ queryKey: ['cms', 'documents'] }),
    ])
  }, [queryClient, id])

  const sitePreview = useSitePreview(() => requestPreview(id))
  const [documentRef, documentWidth] = useElementWidth()

  /**
   * One mutation per action rather than one with a mode flag.
   *
   * They fail differently and they say different things afterwards — a refused publish names a
   * field, a refused schedule names a time — and a single handler branching on a mode is how one
   * of those messages ends up shown for the other action.
   */
  const body = () => ({
    data: draft,
    seo: normalizeSeo(seoDraft),
    expectedVersion: document.version,
    ...(document.revision === undefined ? {} : { expectedRevision: document.revision }),
  })

  const save = useMutation({
    mutationFn: () => saveDocument(id, body()),
    onSuccess: async () => {
      setIssues(null)
      // A save writes the draft. On a published document the site keeps the published copy.
      toast.success(
        'Saved.',
        document.status === 'published'
          ? { description: 'The site shows the change after you publish.' }
          : undefined,
      )
      markClean()
      await invalidate()
      sitePreview.reload()
    },
    onError: showRefusal,
  })

  const publish = useMutation({
    mutationFn: () =>
      saveThenPublish({
        dirty: dirtyRef.current,
        // Clean once the save lands, so a refused publish after it knows the draft is saved.
        save: async () => {
          const saved = await saveDocument(id, body())
          markClean()
          return saved
        },
        publish: () => publishDocument(id),
      }),
    onSuccess: async (result) => {
      setIssues(null)
      markClean()
      // The honest answer to "I published it and the site still shows the old one" — and nothing
      // at all when the publish invalidated nothing, rather than an empty "Refreshed ."
      toast.success(
        'Published.',
        result.invalidated.length === 0
          ? undefined
          : { description: `Refreshed ${result.invalidated.join(', ')}.` },
      )
      await invalidate()
      sitePreview.reload()
    },
    onError: async (error) => {
      showRefusal(error)
      // A refused save keeps the draft dirty. Do not read the document again then: the read
      // replaces the draft, and the editor's unsaved work is gone with no warning.
      if (!dirtyRef.current) await invalidate()
    },
  })

  const unpublish = useMutation({
    mutationFn: () => unpublishDocument(id),
    onSuccess: async () => {
      toast.success('Taken down.', {
        description: 'The version history is untouched, so it can go back up unchanged.',
      })
      await invalidate()
    },
    onError: toastError,
  })

  const schedule = useMutation({
    mutationFn: (scheduledFor: string) => scheduleDocument(id, scheduledFor),
    onSuccess: async (result) => {
      toast.success(statusLine(result))
      await invalidate()
    },
    onError: toastError,
  })

  const rollback = useMutation({
    mutationFn: (version: number) => rollbackDocument(id, version, document.revision),
    onSuccess: async (result) => {
      toast.success(
        `Rolled back. The live version is now ${result.version}.`,
        result.hasUnpublishedChanges
          ? { description: 'Your saved changes that are not published are still in the draft.' }
          : undefined,
      )
      await invalidate()
    },
    onError: toastError,
  })

  const [showVersions, setShowVersions] = useState(false)
  const [takingDown, setTakingDown] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const navigate = useNavigate()

  /**
   * The other collections that fill this page, as cards under the form (`composedInto`). A
   * document with a layout has none here: its sections show their collections.
   */
  const sources = useMemo(
    () => (layout === null ? pageSources(collections.items, document) : []),
    [collections.items, document, layout],
  )

  const busy =
    save.isPending ||
    publish.isPending ||
    unpublish.isPending ||
    schedule.isPending ||
    rollback.isPending

  /**
   * Ctrl+S (⌘S on a Mac) saves the draft, as in a text editor. The browser's own "Save page as"
   * never opens on this page. Nothing is saved while a dialog is open: the keys belong to the
   * dialog's form then, and a save of the page under it would be a surprise.
   */
  const saveDraft = save.mutate
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (!(event.ctrlKey || event.metaKey) || event.key.toLowerCase() !== 's') return
      event.preventDefault()
      if (!canManage || busy) return
      if (globalThis.document.querySelector('[role="dialog"], [role="alertdialog"]') !== null)
        return
      saveDraft()
    }
    globalThis.addEventListener('keydown', onKey)
    return () => globalThis.removeEventListener('keydown', onKey)
  }, [canManage, busy, saveDraft])

  // The site's address, for "View on site" and "Copy link". Only asked for a published page.
  const site = useQuery({ ...siteQuery(), enabled: document.livePath !== null })
  const liveUrl =
    document.livePath === null ? null : siteUrl(site.data?.origin ?? null, document.livePath)

  if (collection === undefined) {
    return (
      <Alert>
        <AlertTitle>Unknown collection</AlertTitle>
        <AlertDescription>
          This document belongs to the <code>{document.collection}</code> collection, which this
          version of the console does not know about. Reload after the next deploy.
        </AlertDescription>
      </Alert>
    )
  }

  const offer = deleteOffer({ canManage, collection, status: document.status })
  const canTakeDown = document.status === 'published' && !isPermanentCollection(collection.key)
  const hasMenu = canTakeDown || offer.offered
  /**
   * An embedded document (a foreword, an FAQ entry, a sponsor) has no page of its own: `pathFor`
   * returns null, the preview falls back to `/`, and the site renders a page with no content.
   * A button whose only result is an empty page is worse than no button.
   */
  const canPreview = collection.routeKind !== 'embedded'
  const errors = issues?.fields ?? NO_ERRORS
  const seoErrors = Object.keys(errors).filter((name) => tabOf(name) === 'seo').length
  const contentErrors = Object.keys(errors).length - seoErrors

  const submitPage = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    save.mutate()
  }

  /**
   * The fields that no section owns, then the page's sections (a `page`) or one card per
   * collection the page shows (a fixed layout).
   *
   * The item dialogs have forms of their own, rendered in a portal. React still passes their
   * submit event up to this form, so the dialog stops it: an Enter key there must not save the
   * page.
   */
  const general = layout === null ? fields : layout.general
  /**
   * With a layout, the canvas is the sections and nothing else: section 1 is the first card. The
   * fields that no section owns (a page's title, Show in the footer) are settings of the
   * document, so they are in the sidebar. Without a layout they are numbered cards, as before.
   */
  const pageGroups = layout === null ? titledGroupCount(general) : 0
  const fieldProps = {
    draft,
    onFieldChange,
    disabled: busy,
    readOnly: !canManage,
    errors,
  }
  const content = (
    <div className="flex min-w-0 flex-col gap-4">
      {layout === null ? (
        <form className="min-w-0" onSubmit={submitPage}>
          <FieldGroups fields={general} {...fieldProps} />
        </form>
      ) : null}
      {layout === null ? (
        sources.map((source, index) => (
          <EmbeddedCollectionSection
            key={source.key}
            collection={source}
            locale={document.locale}
            number={pageGroups + index + 1}
            canManage={canManage}
          />
        ))
      ) : (
        <form className="min-w-0" onSubmit={submitPage}>
          <PageLayoutEditor
            layout={layout}
            collections={collections.items}
            locale={document.locale}
            canManage={canManage}
            startNumber={pageGroups + 1}
            onSectionsChange={(next) => onFieldChange(layout.field.name, next)}
            {...fieldProps}
          />
        </form>
      )}
    </div>
  )

  /**
   * The details column is above the form, not beside it: under 56rem of editor width (the
   * `@4xl/document` line the grid below uses), or with the site preview open. It then closes into
   * one card, so the sections start near the top. Null width: not measured yet, so beside.
   */
  const stacked = sitePreview.open || (documentWidth !== null && documentWidth < STACKED_BELOW_PX)
  const settingsErrors = general.filter((field) => Object.hasOwn(errors, field.name)).length

  return (
    <SitePreviewLayout preview={sitePreview}>
      <div ref={documentRef} className="@container/document flex flex-col gap-4">
        <PageHeader
          title={documentHeading(document, collection)}
          description={documentMeta(document)}
          actions={
            // Two groups that each stay on one line: the reading tools, then the save and the
            // publish. On a narrow editor the groups wrap as two lines, and the reading tools
            // become icons with their names for a screen reader.
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex items-center gap-2">
                {/* For every role: `cms.view` can read the history. Only `cms.manage` restores. */}
                <Button variant="outline" onClick={() => setShowVersions(true)} title="Versions">
                  <HistoryIcon />
                  <span className="@max-2xl/document:sr-only">Versions</span>
                  <span className="tabular-nums">
                    <span className="@max-2xl/document:hidden">(</span>
                    {versions.items.length}
                    <span className="@max-2xl/document:hidden">)</span>
                  </span>
                </Button>
                {canManage && canPreview ? (
                  <Button
                    variant={sitePreview.open ? 'secondary' : 'outline'}
                    onClick={sitePreview.toggle}
                    aria-pressed={sitePreview.open}
                    title="Preview on the site"
                  >
                    <PanelRightIcon />
                    <span className="@max-2xl/document:sr-only">Preview on the site</span>
                  </Button>
                ) : null}
                {liveUrl === null ? null : <LiveLinks url={liveUrl} />}
              </div>
              {canManage ? (
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    onClick={() => save.mutate()}
                    disabled={busy}
                    title={`Save draft (${SAVE_SHORTCUT})`}
                    aria-keyshortcuts="Control+S Meta+S"
                  >
                    {save.isPending ? 'Saving…' : 'Save draft'}
                  </Button>
                  {/* A split button. The rare and destructive actions are in its menu, so the
                      header has one primary action and not four buttons of equal weight. */}
                  <div className="flex">
                    <Button
                      onClick={() => publish.mutate()}
                      disabled={busy}
                      className={hasMenu ? 'rounded-r-none' : undefined}
                    >
                      {publish.isPending
                        ? 'Publishing…'
                        : document.status === 'published'
                          ? 'Publish changes'
                          : 'Publish'}
                    </Button>
                    {hasMenu ? (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            size="icon"
                            aria-label="More publishing actions"
                            disabled={busy}
                            className="border-primary-foreground/20 rounded-l-none border-l"
                          >
                            <ChevronDownIcon />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {canTakeDown ? (
                            <DropdownMenuItem onSelect={() => setTakingDown(true)}>
                              <EyeOffIcon />
                              Take down
                            </DropdownMenuItem>
                          ) : null}
                          {canTakeDown && offer.offered ? <DropdownMenuSeparator /> : null}
                          {offer.offered ? (
                            <DropdownMenuItem
                              variant="destructive"
                              onSelect={() => setDeleting(true)}
                            >
                              <Trash2Icon />
                              Delete {collection.singular.toLowerCase()}…
                            </DropdownMenuItem>
                          ) : null}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </div>
          }
        />

        {canManage ? null : <ReadOnlyNotice area="this document" />}

        {issues === null ? null : (
          <ErrorSummary
            key={refusals}
            issues={issues}
            fields={issueFields}
            reveal={(name) => setTab(tabOf(name))}
            away={(name) => {
              const section = layout?.removedSectionOf(name)
              if (layout === null || section === undefined) return null
              return {
                section: SECTION_INFO[section].label,
                restore: () => onFieldChange(layout.field.name, [...layout.sections, section]),
              }
            }}
          />
        )}

        {/*
          Two columns from 56rem of editor width. With the preview open there is one column: the
          sidebar goes under the form, and the screen is the editor and the preview.
        */}
        <div
          className={cn(
            'grid min-w-0 gap-6',
            sitePreview.open ? null : '@4xl/document:grid-cols-[minmax(0,1fr)_18rem]',
          )}
        >
          <div className="flex min-w-0 flex-col gap-4">
            <div className="min-w-0">
              {collection.seo ? (
                <Tabs
                  value={tab}
                  onValueChange={(value) => setTab(value === 'seo' ? 'seo' : 'content')}
                  className="gap-4"
                >
                  <TabsList variant="line">
                    <TabsTrigger value="content">
                      Layout
                      <TabProblems count={contentErrors} />
                    </TabsTrigger>
                    <TabsTrigger value="seo">
                      SEO
                      <TabProblems count={seoErrors} />
                    </TabsTrigger>
                  </TabsList>
                  {/* Mounted while hidden, like a collapsed card: an upload in progress survives
                      a tab change. */}
                  <TabsContent value="content" forceMount className="data-[state=inactive]:hidden">
                    {content}
                  </TabsContent>
                  <TabsContent value="seo" forceMount className="data-[state=inactive]:hidden">
                    <form className="min-w-0" onSubmit={submitPage}>
                      <SeoEditor
                        seo={seoDraft}
                        onChange={onSeoChange}
                        fallback={seoFallbacks(collection, draft)}
                        path={document.path}
                        disabled={busy}
                        readOnly={!canManage}
                        errors={errors}
                      />
                    </form>
                  </TabsContent>
                </Tabs>
              ) : (
                content
              )}
            </div>
          </div>

          <DocumentSidebar
            document={document}
            collection={collection}
            canManage={canManage}
            disabled={busy}
            scheduleDisabled={busy || !canManage}
            onSchedule={(at) => schedule.mutate(at)}
            stacked={stacked}
            settingsErrors={settingsErrors}
            // Above the form when stacked: the grid has one column, and `order-first` moves it up.
            className={cn('min-w-0 self-start', stacked ? 'order-first self-stretch' : null)}
            {...(layout === null || general.length === 0
              ? {}
              : {
                  settings: (
                    <form className="min-w-0" onSubmit={submitPage}>
                      <FieldRows fields={general} {...fieldProps} />
                    </form>
                  ),
                })}
          />
        </div>

        <VersionHistoryDialog
          open={showVersions}
          onOpenChange={setShowVersions}
          document={document}
          versions={versions.items}
          canRestore={canManage && document.status === 'published'}
          busy={busy}
          restoring={rollback.isPending}
          onRestore={(version, done) => rollback.mutate(version, { onSettled: done })}
          unsaved={dirty}
        />

        {/*
        Taking a page down is destructive to the public, not to the data — the version history is
        untouched — so a confirmation rather than an undo window. The dialog names what it is about
        to do to the live site, because "Are you sure?" answers a question nobody asked.
      */}
        <ConfirmDialog
          open={takingDown}
          onOpenChange={setTakingDown}
          title={`Take “${document.title}” off the site?`}
          description="Visitors will get a 404 until it is published again. The version history is untouched."
          confirmLabel="Take down"
          destructive
          busy={unpublish.isPending}
          onConfirm={() => {
            unpublish.mutate(undefined, { onSettled: () => setTakingDown(false) })
          }}
        />

        {deleting && offer.offered ? (
          <DeleteDocumentDialog
            document={document}
            collection={collection}
            takeDownFirst={offer.takeDownFirst}
            onOpenChange={setDeleting}
            onDeleted={async () => {
              markClean()
              await navigate({ to: '/cms/content', hash: collectionAnchor(collection.key) })
              focusCollection(collection.key)
            }}
          />
        ) : null}

        {/* The unsaved-changes guard, in the console's own dialog rather than the browser's. */}
        <ConfirmDialog
          open={blocker.status === 'blocked'}
          onOpenChange={(open) => {
            if (!open) blocker.reset?.()
          }}
          title="Leave without saving?"
          description="This document has changes that have not been saved. They will be lost."
          confirmLabel="Leave"
          cancelLabel="Stay"
          destructive
          onConfirm={() => blocker.proceed?.()}
        />
      </div>
    </SitePreviewLayout>
  )
}

/** The number of refused fields on a tab, beside the tab's name. */
function TabProblems({ count }: { count: number }) {
  if (count === 0) return null
  return (
    <span className="bg-destructive/10 text-destructive rounded-full px-1.5 text-xs tabular-nums">
      {count}
      <span className="sr-only"> {count === 1 ? 'problem' : 'problems'}</span>
    </span>
  )
}

/** "⌘S" on a Mac, "Ctrl+S" elsewhere: the keys the reader presses. */
const SAVE_SHORTCUT =
  typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.userAgent) ? '⌘S' : 'Ctrl+S'

/**
 * The published page: open it on the site, or copy its address. Two buttons that are icons in a
 * narrow editor, with their names for a screen reader.
 */
function LiveLinks({ url }: { url: string }) {
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url)
      toast.success('Page link copied to clipboard')
    } catch {
      // The browser refused: no permission, or a page that is not secure.
      toast.error('The link was not copied', { description: url })
    }
  }
  return (
    <>
      <Button variant="outline" asChild title="View on site">
        <a href={url} target="_blank" rel="noreferrer">
          <ExternalLinkIcon />
          <span className="@max-2xl/document:sr-only">View on site</span>
          <span className="sr-only"> (opens in a new tab)</span>
        </a>
      </Button>
      <Button variant="outline" size="icon" onClick={() => void copy()} title="Copy link">
        <LinkIcon />
        <span className="sr-only">Copy link</span>
      </Button>
    </>
  )
}
